// REACT AND REACT HOOKS
import React, { useState, useEffect, useRef } from 'react';

// BOOTSTRAP COMPONENTS
import { Modal, Button } from 'react-bootstrap';

// QR CODE GENERATION
import QRCode from 'react-qr-code';

// FONT AWESOME FOR ICONS
import 'font-awesome/css/font-awesome.min.css';

// DATATABLES LIBRARY
import DataTable from 'datatables.net-react';

// CURRENCY INPUT for handling currency fields
import CurrencyInput from 'react-currency-input-field';

// UTILITY FUNCTIONS
import { getPlainName } from '../FP_foodPantryUtils';

// TABLE COLUMN CONFIGURATIONS
import { columnsInventory, columnsTransactions } from '../FP_foodPantryTables';

export function ItemDetailsModal({ show, onHide, itemDetails, filteredItems, transactions, selectedOrgID, setItemDetails, setFilteredItems, setShowItemDetailsModal, showReceiveItemSuccessBanner }) {


  //change later to be inherited
  const url = process.env.REACT_APP_BACKURL;

  // Group transactions by variationName and sum relevant columns
  const filteredItemName = itemDetails[0]?.itemName ? getPlainName(itemDetails[0].itemName) : "";
  const filteredTransactions = filteredItemName
    ? transactions.filter(t => t.itemName === filteredItemName)
    : [];
  const variationTotals = React.useMemo(() => {
    const groups = {};
    filteredTransactions.forEach(t => {
      const vName = t.variationName || "Unknown";
      if (!groups[vName]) {
        groups[vName] = {
          variationName: vName,
          qty: 0,
          totalValue: 0,
          unitPriceSum: 0,
          upc: t.upc || "",
          plu: t.plu || "",
          otherCode: t.otherCode || "",
          count: 0
        };
      }
      groups[vName].qty += parseInt(t.transactionQty, 10) || 0;
      groups[vName].totalValue += parseFloat(t.transactionValue?.replace('$', '')) || 0;
      groups[vName].unitPriceSum += parseFloat(t.unitPrice?.replace('$', '')) || 0;
      groups[vName].upc = t.upc || groups[vName].upc;
      groups[vName].count += 1;
    });
    return Object.values(groups).map(g => ({
      variationName: g.variationName,
      qty: g.qty,
      unitPrice: g.count > 0 ? `$${(g.unitPriceSum / g.count).toFixed(2)}` : "$0.00",
      totalValue: `$${g.totalValue.toFixed(2)}`,
      upc: g.upc,
      plu: g.plu,
      otherCode: g.otherCode
    }));
  }, [filteredTransactions]);

  // Columns for the variation summary table
  const columnsVariationTotals = [
    {
      title: "Variation",
      data: "variationName",
      render: function (data, type, row) {
        return `<a href="#" class="variation-link" data-variation="${encodeURIComponent(JSON.stringify(row))}">${data}</a>`;
      }
    },
    { title: "Total Qty", data: "qty" },
    { title: "Avg Unit Price", data: "unitPrice" },
    { title: "Total Value", data: "totalValue" },
    {
      title: "Code (UPC / PLU / AltID)",
      data: null,
      render: function (data, type, row) {
        // Show UPC, else PLU, else AltID (otherCode)
        if (row.upc && row.upc !== "") return row.upc;
        if (row.plu && row.plu !== "") return row.plu;
        if (row.otherCode && row.otherCode !== "") return row.otherCode;
        return "";
      }
    },
    {
      title: "QR Code",
      data: null,
      orderable: false,
      render: function (data, type, row) {
        const hasCode = (row.upc && row.upc !== "") || (row.plu && row.plu !== "") || (row.otherCode && row.otherCode !== "");
        if (!hasCode) return "";
        return `<button class="btn btn-sm btn-primary qr-code-btn" data-variation="${encodeURIComponent(JSON.stringify(row))}" title="Generate QR Code"><i class="fa fa-qrcode"></i></button>`;
      }
    }
  ];

  const [showVariationModal, setShowVariationModal] = useState(false);
  const [selectedVariationItemDetails, setselectedVariationItemDetails] = useState(null);

  // QR Code Modal state
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [qrCodeVariation, setQRCodeVariation] = useState(null);
  const qrCodeRef = useRef(null);

  const [allVariations, setAllVariations] = useState([]);

  // Call all variations API when showVariationModal modal opens
  useEffect(() => {
    if (showVariationModal) {

      fetch(`${url}/api/foodPantry/inventory/${selectedOrgID}/all-variations`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => {
          setAllVariations(Array.isArray(data) ? data : []);
        })
        .catch(() => setAllVariations([]));
    }
    // Optionally clear variations when modal closes
    if (!showVariationModal) {
      setAllVariations([]);
    }
  }, [showVariationModal, selectedOrgID]);



  useEffect(() => {
    if (!show) return;
    // Delay to ensure DataTable has rendered the links
    const timeout = setTimeout(() => {
      const handler = (e) => {
        e.preventDefault();
        const row = JSON.parse(decodeURIComponent(e.currentTarget.getAttribute('data-variation')));
        setselectedVariationItemDetails(row);
        setShowItemDetailsModal(false);
        setShowVariationModal(true);
      };
      // Remove previous listeners to avoid duplicates
      document.querySelectorAll('.variation-link').forEach(link => {
        link.replaceWith(link.cloneNode(true));
      });
      // Attach new listeners
      document.querySelectorAll('.variation-link').forEach(link => {
        link.addEventListener('click', handler);
      });

      // QR Code button handler
      const qrHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const row = JSON.parse(decodeURIComponent(e.currentTarget.getAttribute('data-variation')));
        setQRCodeVariation(row);
        setShowQRCodeModal(true);
      };
      // Remove previous QR listeners to avoid duplicates
      document.querySelectorAll('.qr-code-btn').forEach(btn => {
        btn.replaceWith(btn.cloneNode(true));
      });
      // Attach new QR listeners
      document.querySelectorAll('.qr-code-btn').forEach(btn => {
        btn.addEventListener('click', qrHandler);
      });
    }, 0);

    // Cleanup
    return () => {
      clearTimeout(timeout);
      document.querySelectorAll('.variation-link').forEach(link => {
        link.replaceWith(link.cloneNode(true));
      });
      document.querySelectorAll('.qr-code-btn').forEach(btn => {
        btn.replaceWith(btn.cloneNode(true));
      });
    };
  }, [variationTotals, show]);


  const [variationInputField, setVariationInputField] = useState({});
  const [variationCodeType, setVariationCodeType] = useState("upc");

  const matchedVariation =
    showVariationModal && selectedVariationItemDetails
      ? allVariations.find(
        v =>
          v.name === selectedVariationItemDetails.variationName &&
          (v.upc === selectedVariationItemDetails.upc ||
            v.plu === selectedVariationItemDetails.plu ||
            v.otherCode === selectedVariationItemDetails.otherCode)
      )
      : null;

  useEffect(() => {
    if (selectedVariationItemDetails && matchedVariation) {
      let parsedPrice = "";
      if (selectedVariationItemDetails.unitPrice) {
        const match = selectedVariationItemDetails.unitPrice.match(/[\d.]+/);
        parsedPrice = match ? match[0] : "";
      }
      // Determine which code type is present and default to it
      let codeType = "upc";
      let codeValue = "";
      if (matchedVariation.upc && matchedVariation.upc !== "") {
        codeType = "upc";
        codeValue = matchedVariation.upc;
      } else if (matchedVariation.plu && matchedVariation.plu !== "") {
        codeType = "plu";
        codeValue = matchedVariation.plu;
      } else if (matchedVariation.otherCode && matchedVariation.otherCode !== "") {
        codeType = "otherCode";
        codeValue = matchedVariation.otherCode;
      }
      setVariationCodeType(codeType);
      setVariationInputField({
        itemVariationID: matchedVariation.itemVariationID || "",
        itemName:
          filteredItemName ||
          (itemDetails[0]?.itemName ? getPlainName(itemDetails[0].itemName) : ""),
        variationName: matchedVariation.name || "",
        suggestedPrice:
          parsedPrice ||
          itemDetails[0]?.suggestedPrice ||
          "",
        upc: matchedVariation.upc || "",
        plu: matchedVariation.plu || "",
        otherCode: matchedVariation.otherCode || ""
      });
    }
  }, [selectedVariationItemDetails, matchedVariation, filteredItemName, itemDetails]);


  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [selectedItemDetails, setSelectedItemDetails] = useState({});
  const [itemInputField, setItemInputField] = useState({});


  const [allCategories, setAllCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);

  // Call all categories API when showCategoryModal modal opens
  useEffect(() => {
    if (showEditItemModal == true) {
      const url = process.env.REACT_APP_BACKURL;
      fetch(url + '/api/foodPantry/itemCategory/' + selectedOrgID, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setAllCategories(Array.isArray(data.categories) ? data.categories : []))
        .catch(() => setAllCategories([]));


    }
  }, [showEditItemModal, selectedOrgID]);

  // Fetch all items when variation edit modal opens
  useEffect(() => {
    if (showVariationModal) {
      const url = process.env.REACT_APP_BACKURL;
      fetch(url + '/api/foodPantry/inventory/' + selectedOrgID + '/items', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        method: 'GET'
      })
        .then(res => res.json())
        .then(data => setAllItems(Array.isArray(data) ? data : []))
        .catch(() => setAllItems([]));
    }
    if (!showVariationModal) {
      setAllItems([]);
    }
  }, [showVariationModal, selectedOrgID]);


  useEffect(() => {
    if (showEditItemModal && itemDetails[0]) {

      setSelectedItemDetails(itemDetails[0]);
    }
  }, [showEditItemModal, itemDetails]);

  useEffect(() => {
    if (showEditItemModal && selectedItemDetails) {
      const unitQty = selectedItemDetails.unitQty || "";
      // Regex to split the numerical size from the unit type
      const matches = unitQty.match(/^(\d*\.?\d*)\s*(.*)$/);
      const unit = matches ? matches[1] : '';
      const unitName = matches ? matches[2] : '';

      setItemInputField({
        itemName: typeof selectedItemDetails.itemName === "string"
          ? getPlainName(selectedItemDetails.itemName)
          : "",
        category: selectedItemDetails.category || "",
        suggestedPrice: selectedItemDetails.suggestedPrice || "",
        unit: unit,
        unitName: unitName,
        packSize: selectedItemDetails.packSize || ""
      });
    }
  }, [showEditItemModal, selectedItemDetails]);



  const [transactionsWithAvg, setTransactionsWithAvg] = useState([]);

  // Calculate and inject average value for each variation on modal open
  useEffect(() => {
    if (!show) {
      setTransactionsWithAvg([]);
      return;
    }

    // Group valid transactions by variationName
    const variationStats = {};
    transactions.forEach(t => {
      const vName = t.variationName || "Unknown";
      // Only consider transactions with totalTransactionValue or unitValue
      let value = 0;
      if (t.totalTransactionValue) {
        value = parseFloat(String(t.totalTransactionValue).replace('$', '')) || 0;
      } else if (t.unitValue) {
        value = (parseFloat(String(t.unitValue).replace('$', '')) || 0) * (parseInt(t.transactionQty, 10) || 0);
      } else {
        return; // skip this transaction
      }
      if (!variationStats[vName]) {
        variationStats[vName] = { totalValue: 0, totalQty: 0 };
      }
      variationStats[vName].totalValue += value;
      variationStats[vName].totalQty += parseInt(t.transactionQty, 10) || 0;
    });

    // Calculate average for each variation
    const variationAverages = {};
    Object.entries(variationStats).forEach(([variationName, stats]) => {
      variationAverages[variationName] =
        stats.totalQty > 0 ? stats.totalValue / stats.totalQty : 0;
    });

    // Inject averageValue into each transaction
    const updatedTransactions = transactions.map(t => ({
      ...t,
      averageValue: '$' + (variationAverages[t.variationName || "Unknown"] || 0).toFixed(2)
    }));

    setTransactionsWithAvg(updatedTransactions);
  }, [show, transactions]);


  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        aria-labelledby="view item details"
        centered
      >
        <div className="text-dark">
          <Modal.Header>
            <div className="d-flex align-items-center justify-content-between w-100">
              <div style={{ visibility: 'hidden' }}>
                {/* An invisible placeholder to align content */}
                <Button
                  variant="light"
                  style={{ border: 'none', background: 'none' }}
                >
                  <i className="fa-solid fa-arrow-left"></i>
                </Button>
              </div>
              <Modal.Title className="text-center w-100">
                <h5 className="ps-0 pt-2 pb-2">
                  {itemDetails.length > 0 ? getPlainName(itemDetails[0].itemName) || "" : ""}
                  {" "}
                  {(() => {
                    const unitQty = filteredItems[0]?.unitQty || "";
                    const packSize = filteredItems[0]?.packSize || "";
                    return (
                      unitQty +
                      (packSize && packSize !== "1" && packSize !== 1 ? " " + packSize + "pk" : "")
                    );
                  })()}
                  <span className="opacity-50">
                    {(() => {
                      return (" (" + variationTotals.reduce((sum, v) => sum + (v.qty || 0), 0) + " OH)");
                    })()}
                  </span>
                </h5>
              </Modal.Title>
              <Button
                variant="light"
                onClick={onHide}
              >
                <i className="fa fa-times"></i>
              </Button>
            </div>
          </Modal.Header>

          <Modal.Body>
            {itemDetails.length > 0 && itemDetails[0] ? (
              <>
                {/* Item Details */}
                {/* redundant and unnecessary info unless more columns get added to justify the space it takes up */}
                {/* <DataTable
                  data={filteredItems}
                  columns={columnsInventory}
                  options={{
                    paging: false,
                    searching: false,
                    ordering: true,
                    responsive: true,
                    dom: 'rt',
                    language: {
                      info: "_START_-_END_ of _TOTAL_ items",
                      infoEmpty: "No data available"
                    },
                    columnDefs: [
                      {
                        targets: [],
                        visible: false,
                        searchable: false
                      }
                    ]
                  }}
                /> */}
                {/* NEW VARIATION TOTALS TABLE */}
                <div className="ps-2 pb-1"><h5>Variations</h5></div>
                <DataTable
                  data={variationTotals}
                  columns={columnsVariationTotals}
                  options={{
                    paging: false,
                    scrollCollapse: true,
                    scrollY: '200px',
                    searching: true,
                    ordering: true,
                    responsive: true,
                    dom: 'Bfrtp',
                    language: {
                      info: "_START_-_END_ of _TOTAL_ variations",
                      infoEmpty: "No variation data available"
                    }
                  }}
                />
                {/* END NEW TABLE */}
                <div className="ps-2 py-1"><h5>Transactions</h5></div>
                <DataTable
                  data={transactionsWithAvg.filter(
                    t => t.itemName === getPlainName(itemDetails[0].itemName)
                  )}
                  columns={columnsTransactions}
                  options={{
                    paging: false,
                    scrollCollapse: true,
                    scrollY: '200px',
                    searching: true,
                    ordering: true,
                    responsive: true,
                    dom: 'Bfrtp',
                    language: {
                      info: "_START_-_END_ of _TOTAL_ transactions",
                      infoEmpty: "No transaction data available",
                      infoFiltered: "(filtered from _MAX_ total transactions)"
                    },
                    columnDefs: [
                      {
                        targets: [],
                        visible: false,
                        searchable: false
                      }
                    ],
                    order: [[1, 'asc']]
                  }}
                />
              </>
            ) : (
              <p className="text-muted">No item selected.</p>
            )}
            <div className="d-flex justify-content-end mt-3">
              <button className='btn btn-primary' onClick={() => { setShowItemDetailsModal(false); setShowEditItemModal(true) }} >Edit Item Details</button>
            </div>
          </Modal.Body>
        </div>
      </Modal>

      {/* Item update modal */}
      <Modal
        show={showEditItemModal}
        onHide={() => setShowEditItemModal(false)}
        size="lg"
        centered
      >
        <Modal.Header>
          <div className="d-flex align-items-center justify-content-between w-100">
            {/* Visible Back Button */}
            <Button
              variant="light"
              onClick={() => {
                // Your logic to go back to the previous modal or view
                setShowEditItemModal(false);
                setShowItemDetailsModal(true);
              }}
            >
              <i className="fa fa-arrow-left"></i>
            </Button>

            {/* Centered Title */}
            <Modal.Title className="text-center flex-grow-1">
              <h5 className="mb-0">
                {itemDetails.length > 0 ? getPlainName(itemDetails[0].itemName) || "" : ""}
                {" "}
                {(() => {
                  const unitQty = filteredItems[0]?.unitQty || "";
                  const packSize = filteredItems[0]?.packSize || "";
                  return (
                    unitQty +
                    (packSize && packSize !== "1" && packSize !== 1 ? " " + packSize + "pk" : "")
                  );
                })()}
              </h5>
            </Modal.Title>

            {/* Visible Close Button */}
            <Button
              variant="light"
              onClick={() => {
                setShowEditItemModal(false);
                setShowItemDetailsModal(false);
              }}
            >
              <i className="fa fa-times"></i>
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div className="pt-3">
              <h5>Edit Item Details</h5>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();

                // Find category by name and get its itemCategoryID
                const existingCategory = allCategories.find(cat => cat.name === itemInputField.category);
                if (!existingCategory) {
                  showReceiveItemSuccessBanner(
                    "Category Not Found",
                    <span>Category not found</span>,
                    "alert"
                  );
                  return;
                }

                // Use itemID from selectedItemDetails (prefer itemID over id)
                const itemID = selectedItemDetails.itemID || selectedItemDetails.id;
                if (!itemID) {
                  showReceiveItemSuccessBanner(
                    "Item ID Not Found",
                    <span>Item ID not found</span>,
                    "alert"
                  );
                  return;
                }

                const unitQty = `${itemInputField.unit}${['oz', 'ct', 'g', 'kg', 'qt', 'lt'].includes(itemInputField.unitName) ? '' : ' '}${itemInputField.unitName}`.trim()

                const itemUpdatePayload = {
                  item: {
                    itemID: itemID,
                    name: itemInputField.itemName,
                    itemCategoryID: existingCategory.itemCategoryID,
                    suggestedPrice: itemInputField.suggestedPrice,
                    unitQty: unitQty,
                    packSize: itemInputField.packSize
                  }
                };



                fetch(url + "/api/foodPantry/inventory/" + selectedOrgID + "/update-item", {
                  method: "PATCH",
                  headers: {
                    "Content-Type": "application/json"
                  },
                  body: JSON.stringify(itemUpdatePayload),
                  credentials: "include"
                })
                  .then(res => res.json())
                  .then((data) => {
                    showReceiveItemSuccessBanner("", itemUpdatePayload.item.name + " successfully updated", "success");
                  })
                  .catch(err => {
                    showReceiveItemSuccessBanner(
                      "Update Failed",
                      <span>Failed to update item: {err.message}</span>,
                      "extreme-error"
                    );
                    console.error(err);
                  });

                setShowEditItemModal(false);
                setShowItemDetailsModal(false);
              }}
              className="mt-3"
            >
              <div className="row g-2">
                <div className="col-md-6 form-floating">
                  <input
                    type="text"
                    className="form-control my-1"
                    value={typeof itemInputField.itemName === "string"
                      ? getPlainName(itemInputField.itemName)
                      : ""}
                    onChange={e =>
                      setItemInputField({ ...itemInputField, itemName: e.target.value })
                    }
                    required
                    placeholder='Item Name'
                  />
                  <label htmlFor="text" className="form-label">
                    Item Name
                  </label>
                </div>
                <div className="col-md-6 form-floating">
                  <input
                    type="text"
                    className="form-control my-1"
                    value={itemInputField?.category || ""}
                    placeholder='Category'
                    onChange={e =>
                      setItemInputField({ ...itemInputField, category: e.target.value })
                    }
                    required
                  />
                  <label htmlFor="text" className="form-label">
                    Category
                  </label>
                </div>
              </div>
              <div className="row g-2">
                <div className="col-md-4 form-floating">
                  <CurrencyInput
                    name="suggestedPrice"
                    placeholder="Price"
                    className="form-control my-1"
                    value={itemInputField?.suggestedPrice || ""}
                    onValueChange={value =>
                      setItemInputField({ ...itemInputField, suggestedPrice: value })
                    }
                    onFocus={e => e.target.select()}
                    autoComplete="off"
                    required
                    allowNegativeValue={false}
                    prefix="$"
                    transformRawValue={rawValue => {
                      // This logic ensures the "cash register" effect
                      const cleanValue = rawValue.replace(/\D/g, "");
                      const paddedValue = cleanValue.padStart(3, "0");
                      const cents = paddedValue.slice(-2);
                      const dollars = paddedValue.slice(0, -2);
                      return `${dollars}.${cents}`;
                    }}
                  />
                  <label htmlFor="text" className="form-label">
                    Price
                  </label>

                </div>
                <div className="col-md-4 form-floating">
                  <div className="input-group">
                    <div className="form-floating">
                      <input
                        type="text"
                        placeholder="Size"
                        className="my-1 form-control"
                        name="unit"
                        value={itemInputField.unit}
                        onChange={e =>
                          setItemInputField({ ...itemInputField, unit: e.target.value })
                        }
                        onFocus={e => e.target.select()}
                        autoComplete="off"
                        required
                      />
                      <label htmlFor="text" className="form-label">
                        Size
                      </label>
                    </div>
                    <select
                      className="form-select"
                      value={itemInputField.unitName}
                      onChange={e =>
                        setItemInputField({ ...itemInputField, unitName: e.target.value })
                      }
                      required
                    >
                      <option value="" disabled hidden>Unit</option>
                      <option value="oz">oz</option>
                      <option value="fl oz">fl oz</option>
                      <option value="lbs">lbs</option>
                      <option value="gal">gal</option>
                      <option value="ct">ct</option>
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="qt">qt</option>
                      <option value="lt">lt</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-4 form-floating">
                  <input
                    type="number"
                    className="form-control my-1"
                    value={itemInputField?.packSize || ""}
                    placeholder='Pack'
                    onChange={e => {
                      // Only allow numbers, max one digit
                      let val = e.target.value.replace(/\D/g, "").slice(0, 1);
                      setItemInputField({ ...itemInputField, packSize: val });
                    }}
                    required
                    maxLength={1}
                  />
                  <label htmlFor="text" className="form-label">
                    Pack
                  </label>
                </div>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <Button type="submit" variant="primary">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>



      {/* variation update modal */}

      < Modal
        show={showVariationModal}
        onHide={() => { setShowVariationModal(false); setShowItemDetailsModal(true); }
        }
        size="lg"
        centered
      >

        <Modal.Header>
          <div className="d-flex align-items-center justify-content-between w-100">
            <Button
              variant="light"
              onClick={() => {
                setShowVariationModal(false);
                setShowItemDetailsModal(true);
              }}
            >
              <i className="fa fa-arrow-left"></i>
            </Button>
            <div className="d-flex justify-content-center w-100">
              {matchedVariation && (
                <h5 className="mb-0">
                  {matchedVariation.name}
                  <span className="ms-2">
                    {(() => {
                      const unitQty = filteredItems[0]?.unitQty || "";
                      let packSize = filteredItems[0]?.packSize || "";
                      if (packSize !== '' && packSize !== 1) packSize = ` ${packSize}pk`;
                      else packSize = '';
                      return unitQty + "" + packSize;

                    })()}
                  </span>
                </h5>
              )}
            </div>
            <Button
              variant="light"
              onClick={() => {
                setShowVariationModal(false);
                setShowItemDetailsModal(false);
              }}
            >
              <i className="fa fa-times"></i>
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="d-flex align-items-center mb-2">
            <h5 className="mb-0 me-3">Edit Variation Details</h5>
            {/* Triple selector for code type */}
            <div className="triple-selector">
              <div className="switch">
                <input
                  id="switch-upc-edit"
                  name="variationCodeTypeEdit"
                  type="radio"
                  value="upc"
                  className="switch-input"
                  checked={variationCodeType === "upc"}
                  onChange={() => setVariationCodeType("upc")}
                />
                <label htmlFor="switch-upc-edit" className="switch-label switch-label-y">UPC</label>
                <input
                  id="switch-plu-edit"
                  name="variationCodeTypeEdit"
                  type="radio"
                  value="plu"
                  className="switch-input"
                  checked={variationCodeType === "plu"}
                  onChange={() => setVariationCodeType("plu")}
                />
                <label htmlFor="switch-plu-edit" className="switch-label switch-label-i">PLU</label>
                <input
                  id="switch-alt-edit"
                  name="variationCodeTypeEdit"
                  type="radio"
                  value="otherCode"
                  className="switch-input"
                  checked={variationCodeType === "otherCode"}
                  onChange={() => setVariationCodeType("otherCode")}
                />
                <label htmlFor="switch-alt-edit" className="switch-label switch-label-n">ALTID</label>
                <span className="switch-selector"></span>
              </div>
            </div>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();

              // Find the item by name (case-insensitive, trimmed)
              const inputName = variationInputField.itemName?.trim().toLowerCase();
              const item = allItems.find(i => i.name && i.name.trim().toLowerCase() === inputName);
              console.log("Matched item for variation update:", item);
              console.log("All items:", allItems);
              if (!item) {
                showReceiveItemSuccessBanner(
                  "Item Not Found",
                  <span>Item name not found in database. Please select a valid item name.</span>,
                  "alert"
                );
                return;
              }

              // Prepare update payload
              const variationUpdatePayload = {
                variation: {
                  itemVariationID: variationInputField.itemVariationID,
                  itemID: item.itemID,
                  name: variationInputField.variationName,
                  suggestedPrice: variationInputField.suggestedPrice,
                  upc: variationCodeType === "upc" ? variationInputField.upc : "",
                  plu: variationCodeType === "plu" ? variationInputField.plu : "",
                  otherCode: variationCodeType === "otherCode" ? variationInputField.otherCode : "",
                  // Always clear the other code fields
                  ...(variationCodeType !== "upc" && { upc: "" }),
                  ...(variationCodeType !== "plu" && { plu: "" }),
                  ...(variationCodeType !== "otherCode" && { otherCode: "" }),
                  type: variationCodeType
                }
              };


              // PATCH request to update variation
              fetch(url + "/api/foodPantry/inventory/" + selectedOrgID + "/update-item-variation", {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json"
                },
                body: JSON.stringify(variationUpdatePayload),
                credentials: "include"
              })
                .then(res => res.json())
                .then(data => {
                  showReceiveItemSuccessBanner("", variationUpdatePayload.variation.name + " successfully updated", "success");
                })
                .catch(err => {
                  showReceiveItemSuccessBanner(
                    "Update Failed",
                    <span>Failed to update variation: {err.message}</span>,
                    "extreme-error"
                  );
                  console.error(err);
                });



              setShowVariationModal(false);
              setShowItemDetailsModal(false);
            }}
            className="mt-3"
          >
            <div className="row g-2">
              <div className="col-md-6 form-floating">
                <input
                  type="text"
                  className="form-control my-1"
                  placeholder='Variation Name'
                  value={variationInputField?.variationName || ""}
                  onChange={(e) => setVariationInputField({ ...variationInputField, variationName: e.target.value })}
                  required
                />
                <label className="form-label">Variation Name</label>
              </div>
              <div className="col-md-6 form-floating">
                {variationCodeType === "upc" && (
                  <input
                    type="text"
                    className="form-control my-1"
                    value={variationInputField?.upc || ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 12);
                      setVariationInputField({ ...variationInputField, upc: val });
                    }}
                    required
                    placeholder='UPC Code'
                    maxLength={12}
                    inputMode="numeric"
                  />
                )}
                {variationCodeType === "plu" && (
                  <input
                    type="text"
                    className="form-control my-1"
                    value={variationInputField?.plu || ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                      setVariationInputField({ ...variationInputField, plu: val });
                    }}
                    required
                    placeholder='PLU Code'
                    maxLength={4}
                    inputMode="numeric"
                  />
                )}
                {variationCodeType === "otherCode" && (
                  <input
                    type="text"
                    className="form-control my-1"
                    value={variationInputField?.otherCode || ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 5);
                      setVariationInputField({ ...variationInputField, otherCode: val });
                    }}
                    required
                    placeholder='ALTID Code'
                    maxLength={5}
                    inputMode="numeric"
                  />
                )}
                <label className="form-label">Product Code</label>
              </div>
            </div>
            <div className="row g-2">
              <div className="col-md-6 form-floating">
                <input
                  type="text"
                  className="form-control my-1"
                  value={variationInputField?.itemName || ""}
                  onChange={(e) => setVariationInputField({ ...variationInputField, itemName: e.target.value })}
                  placeholder='Item Name'
                  required
                />
                <label className="form-label">Item Name</label>
              </div>
              <div className="col-md-6 form-floating">
                <CurrencyInput
                  name="suggestedPrice"
                  placeholder="Suggested Price"
                  className="form-control my-1"
                  value={variationInputField?.suggestedPrice || ""}
                  onValueChange={(value) => setVariationInputField({ ...variationInputField, suggestedPrice: value })}
                  onFocus={(e) => e.target.select()}
                  autoComplete="off"
                  required
                  allowNegativeValue={false}
                  prefix="$"
                  transformRawValue={(rawValue) => {
                    // This logic ensures the "cash register" effect
                    const cleanValue = rawValue.replace(/\D/g, '');
                    const paddedValue = cleanValue.padStart(3, '0');
                    const cents = paddedValue.slice(-2);
                    const dollars = paddedValue.slice(0, -2);
                    return `${dollars}.${cents}`;
                  }}
                />
                <label className="form-label">Suggested Price</label>
              </div>
            </div>
            {/* Validation for code field length */}
            {(() => {
              const isCodeValid =
                (variationCodeType === "upc" && variationInputField.upc && variationInputField.upc.length === 12) ||
                (variationCodeType === "plu" && variationInputField.plu && variationInputField.plu.length === 4) ||
                (variationCodeType === "otherCode" && variationInputField.otherCode && variationInputField.otherCode.length === 5);
              return (
                <div className="d-flex justify-content-end mt-3">
                  <Button type="submit" variant="primary" disabled={!isCodeValid}>
                    Save Changes
                  </Button>
                </div>
              );
            })()}
          </form>
        </Modal.Body>
      </Modal >

      {/* QR Code Modal */}
      <Modal
        show={showQRCodeModal}
        onHide={() => setShowQRCodeModal(false)}
        size="sm"
        centered
      >
        <Modal.Header>
          <div className="d-flex align-items-center justify-content-between w-100">
            <Modal.Title>Product QR Code</Modal.Title>
            <Button
              variant="light"
              size="sm"
              onClick={() => setShowQRCodeModal(false)}
            >
              <i className="fa fa-times"></i>
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="text-center">
          {qrCodeVariation && (() => {
            const productCode = qrCodeVariation.upc || qrCodeVariation.plu || qrCodeVariation.otherCode || "";
            return (
              <div ref={qrCodeRef}>
                <div className="mb-3">
                  <strong>{filteredItemName}</strong>
                  <br />
                  <span className="text-muted">{qrCodeVariation.variationName}</span>
                </div>
                <div className="d-flex justify-content-center mb-3" style={{ background: 'white', padding: '12px' }}>
                  <QRCode value={productCode} size={200} level="H" />
                </div>
                <div className="mb-2">
                  <small className="text-muted">
                    Code: <strong>{productCode}</strong>
                  </small>
                </div>
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button
            variant="primary" className=''
            onClick={() => {
              const printContent = qrCodeRef.current;
              if (!printContent) return;
              const printWindow = window.open('', '_blank');
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Product QR Code - ${filteredItemName}</title>
                    <style>
                      body { 
                        font-family: Arial, sans-serif; 
                        text-align: center; 
                        padding: 20px;
                        margin: 0;
                      }
                      .label-container {
                        border: 1px dashed #ccc;
                        padding: 15px;
                        display: inline-block;
                      }
                      .item-name { font-size: 16px; font-weight: bold; margin-bottom: 5px; }
                      .variation-name { font-size: 14px; color: #666; margin-bottom: 10px; }
                      .qr-container { margin: 10px 0; }
                      .product-code { font-size: 12px; color: #888; margin-top: 10px; }
                      @media print {
                        body { padding: 0; }
                        .label-container { border: none; }
                      }
                    </style>
                  </head>
                  <body>
                    <div class="label-container">
                      <div class="item-name">${filteredItemName}</div>
                      <div class="variation-name">${qrCodeVariation?.variationName || ''}</div>
                      <div class="qr-container">
                        ${printContent.querySelector('svg')?.outerHTML || ''}
                      </div>
                      <div class="product-code">Code: ${qrCodeVariation?.upc || qrCodeVariation?.plu || qrCodeVariation?.otherCode || ''}</div>
                    </div>
                    <script>window.onload = function() { window.print(); window.close(); }</script>
                  </body>
                </html>
              `);
              printWindow.document.close();
            }}
          >
            <i className="fa fa-print me-2"></i>Print
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
